import { axiosInstance } from '@/api/axiosInstance';
import type { PaginatedResponse } from '@/types';

import type { ApplicationData, UserReferenceData } from './Application';
import type { ApplicationBuildData } from './ApplicationBuild';
import type { ApplicationImageData } from './ApplicationImage';
import type { LangfuseProjectWrite } from './features';

const applicationBase = '/applications/v1/application/';
const buildBase = '/applications/v1/application_build/';
const imageBase = '/applications/v1/application_image/';
const userBase = '/br_auth/v1/user/';

export type IdReference = { id: number | string };

export type UuidReference = { uuid: string };

export type ApplicationWritePayload = {
  name: string;
  preview_title: string;
  description: string;
  owner: IdReference | null;
  technical_owner: IdReference | null;
  business_area: UuidReference | null;
  department: string;
  category?: UuidReference | null;
  maturity?: string;
  deployment_environment?: string;
  access_type: string;
  slug: string;
  deployment_method: string;
  external_url: string;
  visibility: string;
  is_archived?: boolean;
  logo?: IdReference | null;
  messaging_enabled?: boolean;
  llm_enabled?: boolean;
  message_senders?: UuidReference[];
  langfuse_project?: LangfuseProjectWrite | null;
};

export type SlugAvailabilityReason = 'invalid' | 'reserved' | 'taken' | null;

export type SlugAvailability = {
  slug: string;
  available: boolean;
  reason: SlugAvailabilityReason;
  message: string;
  url: string | null;
};

export const fetchSlugAvailability = async (
  slug: string,
  { exclude, signal }: { exclude?: string; signal?: AbortSignal } = {},
) => {
  const response = await axiosInstance.get<SlugAvailability>(
    `${applicationBase}slug_availability/`,
    { params: exclude ? { slug, exclude } : { slug }, signal },
  );
  return response.data;
};

export const incrementApplicationViews = async (uuid: string) => {
  const response = await axiosInstance.post<{ view_count: number }>(
    `${applicationBase}${uuid}/increment_views/`,
  );
  return response.data;
};

export const createApplication = async (payload: ApplicationWritePayload) => {
  const response = await axiosInstance.post<ApplicationData>(
    applicationBase,
    payload,
  );
  return response.data;
};

export const updateApplication = async (
  uuid: string,
  payload: Partial<ApplicationWritePayload>,
) => {
  const response = await axiosInstance.patch<ApplicationData>(
    `${applicationBase}${uuid}/`,
    payload,
  );
  return response.data;
};

export const deleteApplication = async (uuid: string) => {
  await axiosInstance.delete(`${applicationBase}${uuid}/`);
};

export const createApplicationBuild = async ({
  application,
  file,
  notes = '',
  isCurrent = true,
}: {
  application: string;
  file: number | string;
  notes?: string;
  isCurrent?: boolean;
}) => {
  const response = await axiosInstance.post<ApplicationBuildData>(buildBase, {
    application: { uuid: application },
    file: { id: file },
    notes,
    is_current: isCurrent,
  });
  return response.data;
};

export const updateApplicationBuild = async (
  uuid: string,
  payload: { notes?: string; is_current?: boolean },
) => {
  const response = await axiosInstance.patch<ApplicationBuildData>(
    `${buildBase}${uuid}/`,
    payload,
  );
  return response.data;
};

export const deleteApplicationBuild = async (uuid: string) => {
  await axiosInstance.delete(`${buildBase}${uuid}/`);
};

export const createApplicationImage = async ({
  application,
  image,
  sortOrder,
}: {
  application: string;
  image: number | string;
  sortOrder: number;
}) => {
  const response = await axiosInstance.post<ApplicationImageData>(imageBase, {
    application: { uuid: application },
    image: { id: image },
    sort_order: sortOrder,
  });
  return response.data;
};

export const deleteApplicationImage = async (uuid: string) => {
  await axiosInstance.delete(`${imageBase}${uuid}/`);
};

export const searchUsers = async (
  search: string,
  { signal, pageSize = 20 }: { signal?: AbortSignal; pageSize?: number } = {},
) => {
  const response = await axiosInstance.get<
    PaginatedResponse<UserReferenceData>
  >(userBase, {
    params: search
      ? { search, page_size: String(pageSize) }
      : { page_size: String(pageSize) },
    signal,
  });
  return response.data.results;
};

export const fetchUser = async (
  id: string,
  { signal }: { signal?: AbortSignal } = {},
) => {
  const response = await axiosInstance.get<UserReferenceData>(
    `${userBase}${id}/`,
    { signal },
  );
  return response.data;
};
